import { useState, type FormEvent } from 'react'
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { BrandLogo } from '../../shared/BrandLogo'
import { useAuth } from '../AuthProvider'
import { Button } from '../../components/ui/Button'
import { GoogleSignInButton } from '../components/GoogleSignInButton'

export function ClientLoginPage() {
  const { login, loginWithGoogle, isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [rememberMe, setRememberMe] = useState(true)
  const [error, setError] = useState('')
  const [notice] = useState(() => {
    const fromState = (location.state as { notice?: string } | null)?.notice
    return (fromState || '').trim()
  })
  const [busy, setBusy] = useState(false)

  if (isAuthenticated) {
    return <Navigate to="/app" replace />
  }

  function goAfterAuth() {
    const from = (location.state as { from?: string } | null)?.from || '/app'
    navigate(from, { replace: true })
  }

  async function onGoogle(credential: string) {
    setBusy(true)
    setError('')
    try {
      await loginWithGoogle(credential, rememberMe)
      goAfterAuth()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Google sign-in failed')
    } finally {
      setBusy(false)
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError('')
    try {
      await login(email.trim(), password, rememberMe)
      goAfterAuth()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="cp-auth cp-auth--login">
      <div className="cp-auth__card cp-auth__card--login">
        <BrandLogo className="cp-auth__logo" tone="light" />
        <p className="cp-eyebrow cp-auth__eyebrow">Client Panel</p>
        <h1 className="cp-auth__title">Sign in</h1>
        <p className="cp-muted cp-auth__description">
          Access your Perfect Predict portal and MetaTrader 5 accounts.
        </p>
        {notice ? (
          <p className="cp-ok cp-auth__alert" role="status">
            {notice}
          </p>
        ) : null}
        {error ? (
          <p className="cp-error cp-auth__alert" role="alert">
            {error}
          </p>
        ) : null}
        <GoogleSignInButton
          mode="signin"
          disabled={busy}
          onCredential={onGoogle}
          onError={setError}
        />
        <form className="cp-auth__form" onSubmit={(e) => void onSubmit(e)}>
          <label className="cp-field">
            <span className="cp-field__label">Email</span>
            <input
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </label>
          <label className="cp-field">
            <span className="cp-field__label-row">
              <span className="cp-field__label">Password</span>
              <Link className="cp-auth__forgot" to="/app/forgot-password">
                Forgot password?
              </Link>
            </span>
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </label>
          <label className="cp-field cp-auth__remember">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
            />
            <span>Keep me signed in</span>
          </label>
          <Button type="submit" className="cp-auth__submit" disabled={busy}>
            {busy ? 'Signing in…' : 'Sign in'}
          </Button>
        </form>
        <p className="cp-auth__signin">
          New here? <Link to="/app/register">Create an account</Link>
        </p>
      </div>
    </div>
  )
}
